/**
 * Wrapper over window.fetch that expects/returns JSON
 */
import { BeamError, NetworkError } from "../utils/beam-errors";
import { logger } from "../utils/logger";
import { version as pkgVersion } from "../../package.json";

export async function fetchJson<T = unknown>(url: RequestInfo, options?: RequestInit): Promise<T> {
  const fetchOptions = {
    method: "GET",
    ...options,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Beam-Client": "WebSDK",
      "X-Beam-Client-Version": pkgVersion,
      ...options?.headers,
    },
    preflightContinue: true,
    mode: options?.mode || "cors",
    body: options?.body != null ? options.body : undefined,
  };

  let response;
  try {
    response = await fetch(url, fetchOptions);
    // fetch can throw a TypeError if the server can't be reached, catch and log below
  } catch (err) {
    const error = new BeamError(`Network call failed (${fetchOptions?.method.toUpperCase()} ${url})`);
    error.cause = err;
    logger.error(error, url);
    throw error;
  }
  let body;
  let text;
  // This can throw a SyntaxError if response payload is malformed JSON
  try {
    text = await response.text();
    if (text) {
      // (response can be empty)
      body = JSON.parse(text);
    }
  } catch (err) {
    let error = err as Error;
    if (err instanceof SyntaxError) {
      error = new NetworkError(response, body, `Response invalid. Expected JSON. Received: "${text}"`);
      error.cause = err;
    }
    logger.error(error);
    throw error;
  }
  // Translate 4xx, 5xx status to runtime error
  if (!response.ok) {
    const error = new NetworkError(response, body, "Response was not OK");
    logger.error(error);
    throw error;
  }

  return body;
}
